/**
 * Card - Container Component for Content Cards
 *
 * Replaces raw divs with card styling.
 * Provides consistent card appearance across the app.
 *
 * Hierarchy: BaseComponent → Box → Card → ListingCard, LocationCard, etc.
 *
 * @example
 * <Card>Basic card</Card>
 * <Card variant="hover">Interactive card</Card>
 * <Card variant="glow">AI-powered card</Card>
 */
import React, { forwardRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/shared/lib/utils/cn";
import { Box } from "@/shared/ui/base/Box";
import type { BaseDivProps } from "@/shared/ui/base";

/**
 * Card variants
 */
const cardVariants = cva(
  // Base card classes
  "bg-white rounded-xl transition-all duration-200",
  {
    variants: {
      variant: {
        // Default: Standard card with shadow
        default: "shadow-md",
        // Hover: Elevates on hover (for interactive cards)
        hover:
          "shadow-md hover:shadow-lg hover:-translate-y-0.5 cursor-pointer",
        // Outline: Border instead of shadow
        outline: "border border-gray-200",
        // Flat: No shadow or border
        flat: "",
        // Glow: Animated gradient border (replaces AIGlowCard)
        glow: "shadow-lg card-ai-glow overflow-visible z-0",
      },
      padding: {
        none: "p-0",
        sm: "p-4",
        md: "p-6",
        lg: "p-8",
      },
    },
    defaultVariants: {
      variant: "default",
      padding: "md",
    },
  },
);

export interface CardProps
  extends BaseDivProps, VariantProps<typeof cardVariants> {
  /** Card header content */
  header?: React.ReactNode;
  /** Card footer content */
  footer?: React.ReactNode;
}

/**
 * Card component
 *
 * Uses Box as the foundation for polymorphic rendering capability.
 * Automatically applies consistent card styling based on variant.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = "default",
      padding = "md",
      className,
      header,
      footer,
      children,
      ...props
    },
    ref,
  ) => {
    const hasHeader = Boolean(header);
    const hasFooter = Boolean(footer);

    // If header/footer exist, we need custom padding structure
    if (hasHeader || hasFooter) {
      return (
        <Box
          ref={ref}
          className={cn(cardVariants({ variant, padding: "none" }), className)}
          {...props}
        >
          {hasHeader && (
            <div className="px-6 py-4 border-b border-gray-100">{header}</div>
          )}
          <div className={cn(cardVariants({ padding }))}>{children}</div>
          {hasFooter && (
            <div className="px-6 py-4 border-t border-gray-100">{footer}</div>
          )}
        </Box>
      );
    }

    // Simple card without header/footer
    return (
      <Box
        ref={ref}
        className={cn(cardVariants({ variant, padding }), className)}
        {...props}
      >
        {children}
      </Box>
    );
  },
);

Card.displayName = "Card";

/**
 * CardHeader - Semantic header section for cards
 */
export const CardHeader = forwardRef<HTMLDivElement, BaseDivProps>(
  ({ className, children, ...props }, ref) => (
    <Box
      ref={ref}
      className={cn("border-b border-gray-100 px-6 py-4", className)}
      {...props}
    >
      {children}
    </Box>
  ),
);

CardHeader.displayName = "CardHeader";

/**
 * CardBody - Semantic body section for cards
 */
export const CardBody = forwardRef<HTMLDivElement, BaseDivProps>(
  ({ className, children, ...props }, ref) => (
    <Box ref={ref} className={cn("p-6", className)} {...props}>
      {children}
    </Box>
  ),
);

CardBody.displayName = "CardBody";

/**
 * CardFooter - Semantic footer section for cards
 */
export const CardFooter = forwardRef<HTMLDivElement, BaseDivProps>(
  ({ className, children, ...props }, ref) => (
    <Box
      ref={ref}
      className={cn("border-t border-gray-100 px-6 py-4", className)}
      {...props}
    >
      {children}
    </Box>
  ),
);

CardFooter.displayName = "CardFooter";
